import { useState, useRef, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import {
  Dumbbell, Mail, ArrowLeft, RefreshCw, CheckCircle,
  Eye, EyeOff, ChevronDown, Check, X,
} from "lucide-react";
import api from "../lib/api";

// ---------------------------------------------------------------------------
// Country dial codes (top countries + comprehensive list)
// ---------------------------------------------------------------------------
const COUNTRIES = [
  { code: "IN", dial: "+91", flag: "\uD83C\uDDEE\uD83C\uDDF3", name: "India" },
  { code: "US", dial: "+1", flag: "\uD83C\uDDFA\uD83C\uDDF8", name: "United States" },
  { code: "GB", dial: "+44", flag: "\uD83C\uDDEC\uD83C\uDDE7", name: "United Kingdom" },
  { code: "AU", dial: "+61", flag: "\uD83C\uDDE6\uD83C\uDDFA", name: "Australia" },
  { code: "CA", dial: "+1", flag: "\uD83C\uDDE8\uD83C\uDDE6", name: "Canada" },
  { code: "AE", dial: "+971", flag: "\uD83C\uDDE6\uD83C\uDDEA", name: "UAE" },
  { code: "SG", dial: "+65", flag: "\uD83C\uDDF8\uD83C\uDDEC", name: "Singapore" },
  { code: "MY", dial: "+60", flag: "\uD83C\uDDF2\uD83C\uDDFE", name: "Malaysia" },
  { code: "SA", dial: "+966", flag: "\uD83C\uDDF8\uD83C\uDDE6", name: "Saudi Arabia" },
  { code: "QA", dial: "+974", flag: "\uD83C\uDDF6\uD83C\uDDE6", name: "Qatar" },
  { code: "KW", dial: "+965", flag: "\uD83C\uDDF0\uD83C\uDDFC", name: "Kuwait" },
  { code: "BH", dial: "+973", flag: "\uD83C\uDDE7\uD83C\uDDED", name: "Bahrain" },
  { code: "OM", dial: "+968", flag: "\uD83C\uDDF4\uD83C\uDDF2", name: "Oman" },
  { code: "DE", dial: "+49", flag: "\uD83C\uDDE9\uD83C\uDDEA", name: "Germany" },
  { code: "FR", dial: "+33", flag: "\uD83C\uDDEB\uD83C\uDDF7", name: "France" },
  { code: "IT", dial: "+39", flag: "\uD83C\uDDEE\uD83C\uDDF9", name: "Italy" },
  { code: "ES", dial: "+34", flag: "\uD83C\uDDEA\uD83C\uDDF8", name: "Spain" },
  { code: "NL", dial: "+31", flag: "\uD83C\uDDF3\uD83C\uDDF1", name: "Netherlands" },
  { code: "JP", dial: "+81", flag: "\uD83C\uDDEF\uD83C\uDDF5", name: "Japan" },
  { code: "KR", dial: "+82", flag: "\uD83C\uDDF0\uD83C\uDDF7", name: "South Korea" },
  { code: "CN", dial: "+86", flag: "\uD83C\uDDE8\uD83C\uDDF3", name: "China" },
  { code: "PK", dial: "+92", flag: "\uD83C\uDDF5\uD83C\uDDF0", name: "Pakistan" },
  { code: "BD", dial: "+880", flag: "\uD83C\uDDE7\uD83C\uDDE9", name: "Bangladesh" },
  { code: "LK", dial: "+94", flag: "\uD83C\uDDF1\uD83C\uDDF0", name: "Sri Lanka" },
  { code: "NP", dial: "+977", flag: "\uD83C\uDDF3\uD83C\uDDF5", name: "Nepal" },
  { code: "ZA", dial: "+27", flag: "\uD83C\uDDFF\uD83C\uDDE6", name: "South Africa" },
  { code: "NG", dial: "+234", flag: "\uD83C\uDDF3\uD83C\uDDEC", name: "Nigeria" },
  { code: "KE", dial: "+254", flag: "\uD83C\uDDF0\uD83C\uDDEA", name: "Kenya" },
  { code: "BR", dial: "+55", flag: "\uD83C\uDDE7\uD83C\uDDF7", name: "Brazil" },
  { code: "MX", dial: "+52", flag: "\uD83C\uDDF2\uD83C\uDDFD", name: "Mexico" },
  { code: "AR", dial: "+54", flag: "\uD83C\uDDE6\uD83C\uDDF7", name: "Argentina" },
  { code: "TR", dial: "+90", flag: "\uD83C\uDDF9\uD83C\uDDF7", name: "Turkey" },
  { code: "EG", dial: "+20", flag: "\uD83C\uDDEA\uD83C\uDDEC", name: "Egypt" },
  { code: "PH", dial: "+63", flag: "\uD83C\uDDF5\uD83C\uDDED", name: "Philippines" },
  { code: "ID", dial: "+62", flag: "\uD83C\uDDEE\uD83C\uDDE9", name: "Indonesia" },
  { code: "TH", dial: "+66", flag: "\uD83C\uDDF9\uD83C\uDDED", name: "Thailand" },
  { code: "VN", dial: "+84", flag: "\uD83C\uDDFB\uD83C\uDDF3", name: "Vietnam" },
];

// ---------------------------------------------------------------------------
// Password validation rules
// ---------------------------------------------------------------------------
const PWD_RULES = [
  { id: "len", label: "At least 8 characters", test: (p) => p.length >= 8 },
  { id: "upper", label: "One uppercase letter (A-Z)", test: (p) => /[A-Z]/.test(p) },
  { id: "lower", label: "One lowercase letter (a-z)", test: (p) => /[a-z]/.test(p) },
  { id: "num", label: "One number (0-9)", test: (p) => /[0-9]/.test(p) },
];

function PasswordStrength({ password }) {
  if (!password) return null;
  const passed = PWD_RULES.filter((r) => r.test(password)).length;
  const pct = (passed / PWD_RULES.length) * 100;
  const color = pct <= 25 ? "bg-red-500" : pct <= 50 ? "bg-orange-500" : pct <= 75 ? "bg-yellow-500" : "bg-green-500";
  const label = pct <= 25 ? "Weak" : pct <= 50 ? "Fair" : pct <= 75 ? "Good" : "Strong";
  return (
    <div className="mt-2 space-y-2">
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-300 ${color}`} style={{ width: `${pct}%` }} />
        </div>
        <span className={`text-xs font-medium ${pct <= 25 ? "text-red-500" : pct <= 50 ? "text-orange-500" : pct <= 75 ? "text-yellow-500" : "text-green-500"
          }`}>{label}</span>
      </div>
      <ul className="space-y-1">
        {PWD_RULES.map((rule) => {
          const ok = rule.test(password);
          return (
            <li key={rule.id} className={`flex items-center gap-1.5 text-xs transition-colors ${ok ? "text-green-500" : "text-muted-foreground"
              }`}>
              {ok
                ? <Check className="h-3 w-3 shrink-0" />
                : <X className="h-3 w-3 shrink-0" />}
              {rule.label}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// International phone picker
// ---------------------------------------------------------------------------
function PhoneField({ value, onChange }) {
  const [dialCode, setDialCode] = useState("+91");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const filtered = COUNTRIES.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.dial.includes(search)
  );

  const selected = COUNTRIES.find((c) => c.dial === dialCode) || COUNTRIES[0];

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const pick = (country) => {
    setDialCode(country.dial);
    setOpen(false);
    setSearch("");
    // Notify parent with full number
    onChange(country.dial + " " + value.replace(/^\+\d+\s*/, ""));
  };

  const handleNumber = (e) => {
    const raw = e.target.value.replace(/\D/g, "");
    onChange(dialCode + " " + raw);
  };

  const numberOnly = value.replace(/^\+\d+\s*/, "");

  return (
    <div className="flex gap-2 mt-1" ref={dropdownRef}>
      {/* Dial code selector */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1.5 h-10 px-3 rounded-sm bg-secondary border border-input text-sm font-medium hover:bg-secondary/80 transition-colors min-w-[82px] justify-between"
          data-testid="phone-dial-button"
        >
          <span className="text-base leading-none">{selected.flag}</span>
          <span className="text-muted-foreground">{dialCode}</span>
          <ChevronDown className={`h-3 w-3 text-muted-foreground transition-transform ${open ? "rotate-180" : ""
            }`} />
        </button>

        {open && (
          <div className="absolute z-50 top-11 left-0 w-64 bg-card border border-border rounded-sm shadow-xl">
            {/* Search */}
            <div className="p-2 border-b border-border">
              <Input
                placeholder="Search country or code…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 text-xs rounded-sm"
                autoFocus
              />
            </div>
            {/* List */}
            <ul className="max-h-52 overflow-y-auto py-1" role="listbox">
              {filtered.length === 0 && (
                <li className="px-3 py-2 text-xs text-muted-foreground">No results</li>
              )}
              {filtered.map((c) => (
                <li
                  key={c.code}
                  role="option"
                  aria-selected={c.dial === dialCode}
                  onClick={() => pick(c)}
                  className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer text-sm hover:bg-secondary transition-colors ${c.dial === dialCode ? "bg-secondary" : ""
                    }`}
                >
                  <span className="text-base w-6 shrink-0">{c.flag}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-muted-foreground text-xs shrink-0">{c.dial}</span>
                  {c.dial === dialCode && <Check className="h-3 w-3 text-primary shrink-0" />}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Number input */}
      <Input
        type="tel"
        inputMode="numeric"
        value={numberOnly}
        onChange={handleNumber}
        placeholder="98765 43210"
        className="rounded-sm flex-1"
        data-testid="signup-phone-input"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step constants
// ---------------------------------------------------------------------------
const STEP_FORM = "form";
const STEP_OTP = "otp";
const STEP_DONE = "done";

// ---------------------------------------------------------------------------
// Main Signup component
// ---------------------------------------------------------------------------
export default function Signup() {
  const [step, setStep] = useState(STEP_FORM);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", gymName: "" });
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [pwdFocused, setPwdFocused] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [signupPrevented, setSignupPrevented] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);

  const { signup } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/signup-status")
      .then((res) => {
        if (res.data.preventSignup) {
          setSignupPrevented(true);
        }
      })
      .catch((err) => {
        console.error("Failed to check signup status:", err);
      })
      .finally(() => {
        setCheckingStatus(false);
      });
  }, []);

  const update = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const isPasswordValid = PWD_RULES.every((r) => r.test(form.password));

  // Resend countdown
  const startResendTimer = () => {
    setResendTimer(60);
    const id = setInterval(() => {
      setResendTimer((t) => { if (t <= 1) { clearInterval(id); return 0; } return t - 1; });
    }, 1000);
  };

  // Step 1: send OTP
  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!isPasswordValid) {
      toast.error("Please meet all password requirements.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/send-otp", { email: form.email, name: form.name });
      toast.success("Verification code sent! Check your inbox.");
      setStep(STEP_OTP);
      startResendTimer();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResend = async () => {
    if (resendTimer > 0) return;
    setLoading(true);
    try {
      await api.post("/auth/send-otp", { email: form.email, name: form.name });
      toast.success("New verification code sent!");
      startResendTimer();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { toast.error("Please enter the 6-digit code."); return; }
    setLoading(true);
    try {
      await api.post("/auth/verify-otp", { email: form.email, otp });
      toast.success("Email verified! Creating your account…");
      setStep(STEP_DONE);
      await handleCreateAccount();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  // Step 3: create account
  const handleCreateAccount = async () => {
    setLoading(true);
    try {
      await signup(form);
      toast.success("Account created! Welcome to Optimus Gym.");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
      setStep(STEP_FORM);
    } finally {
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="flex items-center justify-center min-h-[100dvh] w-full bg-background" data-testid="signup-loading">
        <span className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div
      className="items-center justify-center p-2 md:p-4 overflow-y-auto overflow-x-hidden overflow-hidden bg-background flex flex-col h-[100dvh] w-full"
      style={{
        backgroundImage:
          "linear-gradient(to right, #80808012 1px, transparent 1px), linear-gradient(to bottom, #80808012 1px, transparent 1px)",
        backgroundSize: "24px 24px",
      }}
    >
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center mb-8 gap-3">
          <Dumbbell className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-black tracking-tight font-heading text-primary">OPTIMUS GYM</h1>
        </div>

        {/* ── STEP: FORM ──────────────────────────────────────────────────── */}
        {step === STEP_FORM && (
          signupPrevented ? (
            <Card className="border-border bg-card rounded-sm" data-testid="registrations-closed-card">
              <CardHeader>
                <CardTitle className="text-xl font-heading text-destructive flex items-center gap-2">
                  <X className="h-5 w-5" /> Registrations Closed
                </CardTitle>
                <CardDescription>
                  New organization registrations are currently disabled by the platform administrator.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  If you believe this is an error or would like to request an account, please contact the support team.
                </p>
                <Button className="w-full rounded-sm h-11 font-heading uppercase tracking-wider" asChild>
                  <Link to="/login">Back to Sign In</Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-border bg-card rounded-sm">
              <CardHeader>
                <CardTitle className="text-xl font-heading">Create Account</CardTitle>
                <CardDescription>Fill in your details to get started</CardDescription>
              </CardHeader>
              <CardContent >
                <form onSubmit={handleSendOTP} className="space-y-4">

                  {/* Name */}
                  <div>
                    <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                      Your Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="John Doe"
                      required
                      className="rounded-sm mt-1 h-11 bg-secondary border-0 focus:ring-2 focus:ring-primary/30"
                      data-testid="signup-name-input"
                    />
                  </div>

                  {/* Gym Name */}
                  <div>
                    <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Gym Name</Label>
                    <Input
                      value={form.gymName}
                      onChange={(e) => update("gymName", e.target.value)}
                      placeholder="Iron Paradise Gym"
                      className="rounded-sm mt-1 h-11 bg-secondary border-0 focus:ring-2 focus:ring-primary/30"
                      data-testid="signup-gym-input"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                      Email <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="you@gym.com"
                      required
                      className="rounded-sm mt-1 h-11 bg-secondary border-0 focus:ring-2 focus:ring-primary/30"
                      data-testid="signup-email-input"
                    />
                  </div>

                  {/* Phone — international picker */}
                  <div>
                    <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Phone</Label>
                    <PhoneField
                      value={form.phone}
                      onChange={(val) => update("phone", val)}
                    />
                  </div>

                  {/* Password */}
                  <div>
                    <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                      Password <span className="text-destructive">*</span>
                    </Label>
                    <div className="relative mt-1">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={form.password}
                        onChange={(e) => update("password", e.target.value)}
                        onFocus={() => setPwdFocused(true)}
                        onBlur={() => setPwdFocused(false)}
                        placeholder="Min 8 characters"
                        required
                        className="rounded-sm h-11 bg-secondary border-0 focus:ring-2 focus:ring-primary/30 pr-11"
                        data-testid="signup-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((s) => !s)}
                        tabIndex={-1}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>

                    {/* Password strength + rules — show when focused or has value */}
                    {(pwdFocused || form.password) && (
                      <PasswordStrength password={form.password} />
                    )}
                  </div>

                  <Button
                    type="submit"
                    className="w-full rounded-sm h-11 font-heading uppercase tracking-wider"
                    disabled={loading || !isPasswordValid}
                    data-testid="signup-submit-button"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full" />
                        Sending code…
                      </span>
                    ) : "Continue"}
                  </Button>
                </form>

                <p className="mt-4 text-center text-sm text-muted-foreground">
                  Already have an account?{" "}
                  <Link to="/login" className="text-primary hover:underline" data-testid="login-link">
                    Sign In
                  </Link>
                </p>
              </CardContent>
            </Card>
          )
        )}

        {/* ── STEP: OTP ──────────────────────────────────────────────────── */}
        {step === STEP_OTP && (
          <Card className="border-border bg-card rounded-sm">
            <CardHeader>
              <div className="flex items-center gap-2 mb-1">
                <button
                  onClick={() => setStep(STEP_FORM)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Go back"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <CardTitle className="text-xl font-heading">Verify your email</CardTitle>
              </div>
              <CardDescription className="flex items-center gap-2 pt-1">
                <Mail className="h-4 w-4 shrink-0" />
                <span>
                  We sent a 6-digit code to{" "}
                  <strong className="text-foreground">{form.email}</strong>
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleVerifyOTP} className="space-y-5">
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                    Verification Code
                  </Label>
                  <Input
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="123456"
                    inputMode="numeric"
                    maxLength={6}
                    className="rounded-sm mt-1 text-center text-2xl font-mono tracking-widest h-14 bg-secondary border-0 focus:ring-2 focus:ring-primary/30"
                    autoFocus
                    data-testid="otp-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Code expires in 10 minutes</p>
                </div>

                <Button
                  type="submit"
                  className="w-full rounded-sm h-11 font-heading uppercase tracking-wider"
                  disabled={loading || otp.length !== 6}
                  data-testid="verify-otp-button"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full" />
                      Verifying…
                    </span>
                  ) : "Verify & Create Account"}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <button
                  onClick={handleResend}
                  disabled={resendTimer > 0 || loading}
                  className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="resend-otp-button"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  {resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend code"}
                </button>
              </div>

              <p className="mt-3 text-center text-sm text-muted-foreground">
                Wrong email?{" "}
                <button onClick={() => setStep(STEP_FORM)} className="text-primary hover:underline">
                  Go back
                </button>
              </p>
            </CardContent>
          </Card>
        )}

        {/* ── STEP: DONE ──────────────────────────────────────────────────── */}
        {step === STEP_DONE && (
          <Card className="border-border bg-card rounded-sm">
            <CardContent className="pt-10 pb-10 flex flex-col items-center gap-4">
              <CheckCircle className="h-14 w-14 text-green-500" />
              <p className="text-lg font-semibold">Email verified!</p>
              <p className="text-sm text-muted-foreground">Setting up your account…</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
